/**
 * The curtain: walls, gatehouses and towers, drawn as solids.
 *
 * What was here before was a coloured band with a lighter strip along its top
 * edge and three merlon nubs. At tactical zoom that is fine, because everything
 * is a token at tactical zoom; at close zoom it is a strip of cardboard laid on
 * the ground, and it was the one thing on the field with no thickness at all
 * while every building beside it had a roof, a lit face and a shadow.
 *
 * ## The projection
 *
 * This game is **top-down ground with upward extrusion**, and it always has
 * been — `drawBuilding` puts a footprint on the tile and raises the mass into
 * padding *above* it, lit from the upper left, with a cast shadow down and to
 * the right. That is the convention; nothing here invents a second one. A true
 * isometric curtain would be a different renderer, a different terrain bake and
 * a different unit set, and it would be the only isometric thing in the game.
 *
 * So a wall is drawn the way a building is:
 *
 *   • a **cast shadow** on the ground, offset down-right, which is what stops it
 *     floating;
 *   • the **outer face** — the south side, the one an orthographic top-down view
 *     can actually see — in shade, with coursing;
 *   • the **walkway** on top, the footprint lifted by `WALL_RISE`, lit;
 *   • **merlons** along the far edge, so the silhouette breaks against the
 *     ground beyond it.
 *
 * **`WALL_RISE` and `TOWER_RISE` are shared with the garrison lift, and that is
 * the point of exporting them.** `drawUnits` has always raised a man posted on a
 * wall by 0.42 of a tile and a man on a tower by 0.78 — against stonework that
 * was drawn flat on the ground. Every archer on the curtain has therefore been
 * hovering a third of a tile above it since garrisons landed. One constant,
 * read by the art and by the lift, and they cannot drift apart again.
 *
 * ## Three tiers, three materials
 *
 * `WALL_TIERS` has been Palisade, Stone Wall and Bastion since the day walls
 * stopped being one thing you could or could not afford — sharpened logs, then
 * ashlar, then something thick enough that the sappers have to mean it. The
 * renderer drew all three as the same grey band, so the most legible upgrade
 * decision in the game ("bastions across the northern approach, palisade
 * everywhere else") was invisible on the map it is made on. They are three
 * different drawings now, and which stretch a player has paid for is readable
 * from across the settlement.
 *
 * The stone comes from the people's own `Vernacular`, so a Karrun-Dûr circuit is
 * dwarf-coloured and batters like their houses do.
 */

import type { Graphics } from 'pixi.js';
import { mix, shade } from '../data/palette.ts';
import type { Vernacular } from './vernacular.ts';

/**
 * How far a wall's walkway stands above its own footprint, in tiles.
 *
 * Read by `drawUnits` for the garrison lift as well. See the note above: these
 * were two numbers before, and one of them was zero.
 */
export const WALL_RISE = 0.42;

/** The same, for a tower. A tower is roughly twice a wall. */
export const TOWER_RISE = 0.78;

/** The ink a stretch of curtain is drawn in, once its damage is folded in. */
interface Ink {
  /** The outer face, away from the light. */
  face: number;
  /** The walkway on top. */
  top: number;
  /** The coping along the parapet, the brightest plane. */
  coping: number;
  /** Mortar courses and the joints between merlons. */
  joint: number;
  /** The shadowed foot where the stone meets the ground. */
  foot: number;
}

/** Damaged stone goes toward this: scorched, blood-dark, wrong. */
const HURT = 0x6b4a44;

/**
 * A tier's ink, blended toward `HURT` by how badly the stretch is hurt.
 *
 * The damage blend is on the *material*, not a tint over the finished sprite,
 * which is what lets the coursing and the merlon shadows stay readable on a
 * wall that is nearly down — a flat tint over the whole thing turns a battered
 * bastion into a smooth pink slab, which is what the old sprite tint did.
 */
function inkFor(tier: number, frac: number, v: Vernacular): Ink {
  // A palisade is timber, and timber is the one part of a curtain that is not
  // the people's masonry — everybody sharpens logs the same way.
  // Lifted well clear of the masonry, because a palisade that is merely a
  // slightly browner grey is a palisade nobody can pick out of a circuit.
  const base = tier <= 1 ? shade(v.timber, 1.25) : v.wall;
  const litBase = tier <= 1 ? shade(v.timber, 1.95) : v.wallLit;
  /**
   * Five planes, and the gap between `face` and `top` is the whole illusion.
   *
   * The first cut put the face at 0.82 of the wall colour, which on ground this
   * dark read as a black gap under a bright slab rather than as the side of
   * something. A face is in shadow, not in a hole: it sits a little under the
   * walkway and a long way above the ground.
   */
  return {
    face: mix(HURT, shade(base, 1.02), frac),
    top: mix(HURT, shade(litBase, 0.92), frac),
    coping: mix(HURT, shade(litBase, 1.24), frac),
    joint: mix(0x2a1c19, shade(base, 0.6), frac),
    foot: mix(0x241a17, shade(base, 0.42), frac),
  };
}

/**
 * How thick a tier's stone is across the run, as a fraction of a tile.
 *
 * The spread is deliberately wide. Thickness is the one property of a wall that
 * is legible at *tactical* zoom, where a merlon is half a pixel and the coursing
 * is gone — a bastion has to be visibly a heavier line than a palisade from as
 * far out as the player can see the circuit at all, because "which stretch did I
 * pay for" is a question asked while looking at the whole ring.
 */
function thickness(tier: number): number {
  return tier >= 3 ? 0.8 : tier === 2 ? 0.62 : 0.42;
}

/**
 * One tile of curtain.
 *
 * `n`/`e`/`s`/`w` are whether the neighbour on that side is also curtain. They
 * decide the shape of the stone — a run, a corner, a T — and, more importantly,
 * which edges are *exposed*: an edge with a neighbour against it has no face to
 * draw, and drawing one there puts a wall of masonry across the inside of a
 * straight run.
 */
export function drawWallTile(
  g: Graphics,
  sx: number,
  sy: number,
  z: number,
  tier: number,
  frac: number,
  n: boolean,
  e: boolean,
  s: boolean,
  w: boolean,
  vern: Vernacular,
): void {
  const ink = inkFor(tier, frac, vern);
  const core = z * thickness(tier);
  const half = core / 2;
  const cx = sx + z / 2;
  const cy = sy + z / 2;
  // A hair over half a tile, so two neighbours overlap rather than leaving a
  // seam at fractional zooms.
  const arm = z / 2 + 1;
  const rise = z * WALL_RISE;
  // A bastion's foot is battered — it stands out further than its top, which is
  // what "thick enough that the sappers have to mean it" looks like from above.
  const talus = tier >= 3 ? z * 0.1 : 0;

  const horiz = e || w;
  const vert = n || s;
  const hx0 = w ? cx - arm : cx - half;
  const hx1 = e ? cx + arm : cx + half;
  const vy0 = n ? cy - arm : cy - half;
  const vy1 = s ? cy + arm : cy + half;

  /** The footprint, as one or two bands. An isolated stub is the core box. */
  const bands: Array<[number, number, number, number]> = [];
  if (horiz || !vert) bands.push([hx0, cy - half, hx1 - hx0, core]);
  if (vert) bands.push([cx - half, vy0, core, vy1 - vy0]);

  // ── The shadow on the ground ─────────────────────────────────────────────
  const off = z * 0.09;
  for (const [bx, by, bw, bh] of bands) {
    g.rect(bx + off, by + off, bw, bh).fill({ color: 0x000000, alpha: 0.36 });
  }

  /**
   * ── The outer face ───────────────────────────────────────────────────────
   *
   * Only the south side is drawn, because only the south side exists in an
   * orthographic top-down view: the east and west faces of an extruded solid
   * are edge-on and have no width at all. They get a dark outline instead, at
   * the end.
   *
   * The segments are the parts of the footprint's southern boundary with no
   * stone beyond them. On a straight east–west run that is the whole width; on
   * a tile with a neighbour to the south it is the two arms and not the middle,
   * which is what keeps a corner from growing a face across its own inside.
   */
  const segs: Array<[number, number]> = [];
  if (horiz) {
    if (s) {
      if (w) segs.push([hx0, cx - half]);
      if (e) segs.push([cx + half, hx1]);
    } else {
      segs.push([hx0, hx1]);
    }
  } else if (!s) {
    segs.push([cx - half, cx + half]);
  }

  const faceTop = cy + half - rise;
  for (const [x0, x1] of segs) {
    const bw = x1 - x0;
    if (bw <= 0) continue;
    if (tier <= 1) {
      drawPalisadeFace(g, x0, faceTop, bw, rise, z, ink);
    } else {
      // Battered: the foot reaches further south than the top does.
      if (talus > 0) {
        g.poly([x0, cy + half, x1, cy + half, x1, cy + half + talus, x0, cy + half + talus]).fill(ink.foot);
      }
      g.rect(x0, faceTop, bw, rise).fill(ink.face);
      courses(g, x0, faceTop, bw, rise, z, ink, tier);
      // The dark line where the stone meets the ground it stands on.
      g.rect(x0, cy + half - Math.max(1, z * 0.05), bw, Math.max(1, z * 0.05)).fill(ink.foot);
    }
  }

  // ── The walkway ──────────────────────────────────────────────────────────
  for (const [bx, by, bw, bh] of bands) {
    g.rect(bx, by - rise, bw, bh).fill(ink.top);
  }
  // The coping: a bright lip along the northern edge of every band, which is
  // the edge the light is coming over.
  const cap = Math.max(1, z * 0.09);
  for (const [bx, by, bw] of bands) {
    g.rect(bx, by - rise, bw, cap).fill(ink.coping);
  }

  // ── Merlons ──────────────────────────────────────────────────────────────
  //
  // Along the far edge, and only once a tile is wide enough that they are more
  // than aliasing. A palisade gets points instead: it is sharpened logs, and a
  // row of little squares on top of one is a battlement it does not have.
  if (z >= 15) {
    for (const [bx, by, bw, bh] of bands) {
      const alongX = bw >= bh;
      const runLen = alongX ? bw : bh;
      const step = Math.max(z * 0.3, 5);
      const count = Math.max(1, Math.round(runLen / step));
      const mw = (runLen / count) * 0.58;
      const mh = Math.max(1.5, z * 0.16);
      for (let i = 0; i < count; i++) {
        const t = bx + 0 + (alongX ? (i + 0.21) * (runLen / count) : 0);
        if (alongX) {
          const mx = bx + (i + 0.21) * (runLen / count);
          if (tier <= 1) {
            g.poly([mx, by - rise, mx + mw, by - rise, mx + mw / 2, by - rise - mh]).fill(ink.coping);
          } else {
            g.rect(mx, by - rise - mh, mw, mh + cap * 0.6).fill(ink.coping);
            g.rect(mx, by - rise - mh, mw, Math.max(1, mh * 0.3)).fill(ink.joint);
          }
        } else {
          // A north–south run is seen along its length, so the merlons are the
          // two kerbs either side of the walkway rather than a row across it.
          const my = by - rise + (i + 0.21) * (runLen / count);
          const kw = Math.max(1.2, z * 0.1);
          g.rect(bx, my, kw, mw).fill(ink.coping);
          g.rect(bx + bh - kw, my, kw, mw).fill(ink.coping);
          void t;
        }
      }
    }
  }

  /**
   * The ends, capped.
   *
   * **Only the exposed ones.** The first cut stroked the whole of every band's
   * rectangle, and since every tile draws its own band, that put a dark vertical
   * line on every tile boundary down the length of a run — a hundred-tile
   * curtain rendered as a hundred numbered panels. An edge with stone against it
   * is not an edge.
   */
  if (z >= 11) {
    const lw = Math.max(1, z * 0.05);
    if (horiz) {
      if (!w) g.rect(hx0, cy - half - rise, lw, core + rise).fill({ color: 0x1a1714, alpha: 0.8 });
      if (!e) g.rect(hx1 - lw, cy - half - rise, lw, core + rise).fill({ color: 0x1a1714, alpha: 0.8 });
    }
    if (vert) {
      if (!n) g.rect(cx - half, vy0 - rise, core, lw).fill({ color: 0x1a1714, alpha: 0.8 });
    }
    // The shadow the parapet throws back across its own walkway. Two pixels of
    // it is the difference between a top surface and a coloured rectangle.
    for (const [bx, by, bw, bh] of bands) {
      g.rect(bx, by - rise + cap, bw, Math.max(1, z * 0.045)).fill({ color: 0x000000, alpha: 0.28 });
      // The near parapet, lit along its top. A wall has a kerb on both sides —
      // men walk *between* them — and without this one the walkway runs
      // straight off its own edge into the face and reads as a slab with a
      // stripe on it.
      g.rect(bx, by - rise + bh - cap, bw, cap).fill(ink.coping);
    }
  }
}

/**
 * A palisade's face: upright logs, not a slab.
 *
 * Half a dozen strips with alternating tone and a rounded head each, because
 * the whole read of tier one is *this is wood and it is temporary*. Below the
 * zoom where a log is two pixels it collapses to a plain band, which is what
 * every other detail in this renderer does.
 */
function drawPalisadeFace(
  g: Graphics,
  x0: number,
  top: number,
  width: number,
  rise: number,
  z: number,
  ink: Ink,
): void {
  g.rect(x0, top, width, rise).fill(ink.face);
  if (z < 20) return;
  const logW = Math.max(2.4, z * 0.13);
  const count = Math.max(1, Math.floor(width / logW));
  const step = width / count;
  for (let i = 0; i < count; i++) {
    const lx = x0 + i * step;
    // Every other log a shade lighter: a palisade is whatever the woodcutters
    // brought in, and a wall of identical staves reads as a fence.
    if (i % 2 === 0) g.rect(lx, top, step * 0.82, rise).fill(ink.top);
    g.rect(lx + step * 0.82, top, Math.max(1, step * 0.18), rise).fill(ink.joint);
  }
}

/**
 * Mortar courses on a stone face.
 *
 * Two or three horizontal joints with the verticals staggered between them —
 * staggered because a stack of aligned verticals is a grid, and a grid is the
 * one thing masonry never looks like. Gated on zoom: at fourteen pixels a tile
 * these lines are the whole face.
 */
function courses(
  g: Graphics,
  x0: number,
  top: number,
  width: number,
  rise: number,
  z: number,
  ink: Ink,
  tier: number,
): void {
  if (z < 20 || rise < 5) return;
  const rows = tier >= 3 ? 2 : 3;
  const lw = Math.max(0.8, z * 0.022);
  const rowH = rise / rows;
  for (let r = 1; r < rows; r++) {
    g.rect(x0, top + r * rowH - lw / 2, width, lw).fill({ color: ink.joint, alpha: 0.75 });
  }
  const blockW = Math.max(4, z * (tier >= 3 ? 0.3 : 0.2));
  for (let r = 0; r < rows; r++) {
    const y = top + r * rowH;
    const offset = (r % 2) * (blockW / 2);
    for (let x = x0 + offset; x < x0 + width; x += blockW) {
      if (x <= x0) continue;
      g.rect(x, y, lw, rowH).fill({ color: ink.joint, alpha: 0.6 });
    }
  }
  // A string course under the parapet on a bastion — the one ornament the top
  // tier gets, and the thing that says "somebody paid for this".
  if (tier >= 3) {
    g.rect(x0, top + Math.max(1, z * 0.03), width, Math.max(1, z * 0.045)).fill({
      color: ink.coping,
      alpha: 0.55,
    });
  }
}

/**
 * A tower: the 2×2 block, drawn once from its north-west tile.
 *
 * It is the tallest thing on a circuit and the thing a player looks for when
 * asking where their archers are, so it gets the silhouette budget: a battered
 * base, a coursed shaft, a **corbelled crown that overhangs the shaft**, and
 * merlons on top of that. The overhang is doing the most work of the four —
 * a box with crenellations on it is a box, and a box with a wider lid is a
 * tower, at any zoom where the lid is two pixels proud.
 */
export function drawTower(
  g: Graphics,
  sx: number,
  sy: number,
  z: number,
  size: number,
  frac: number,
  vern: Vernacular,
): void {
  const ink = inkFor(3, frac, vern);
  const wpx = z * size;
  const hpx = z * size;
  const rise = z * TOWER_RISE;
  const talus = z * 0.12;

  // Cast shadow, so it sits above the ground rather than on it.
  g.rect(sx + wpx * 0.11, sy + hpx * 0.15, wpx, hpx).fill({ color: 0x000000, alpha: 0.4 });

  // The battered foot: wider than the shaft, and the reason the base does not
  // read as a sticker edge.
  g.poly([
    sx - talus * 0.5, sy + hpx + talus,
    sx + wpx + talus * 0.5, sy + hpx + talus,
    sx + wpx, sy + hpx - rise * 0.25,
    sx, sy + hpx - rise * 0.25,
  ]).fill(ink.foot);

  // The shaft: the footprint pushed up by `rise`.
  const shaftTop = sy - rise;
  g.rect(sx, shaftTop, wpx, hpx + rise).fill(ink.face);
  // Lit on the left, because everything in this renderer is.
  g.rect(sx, shaftTop, wpx * 0.38, hpx + rise).fill(ink.top);
  courses(g, sx, sy + hpx - rise * 0.9, wpx, rise * 0.9, z, ink, 3);

  // Arrow slits: two, on the face, so the silhouette says what it is for. A
  // cross-slit rather than a bar — it is the shape that reads as a loophole and
  // not as a window.
  if (z >= 16) {
    const sw = Math.max(1.4, z * 0.09);
    for (const fx of [0.3, 0.66]) {
      const lx = sx + wpx * fx - sw / 2;
      const ly = sy + hpx - rise * 0.72;
      g.rect(lx, ly, sw, rise * 0.5).fill(0x100e0c);
      g.rect(lx - sw, ly + rise * 0.16, sw * 3, sw).fill(0x100e0c);
    }
  }

  // The crown, corbelled out past the shaft on every side.
  const over = z * 0.16;
  const crownH = Math.max(2.5, z * 0.26);
  g.rect(sx - over, shaftTop - crownH * 0.35, wpx + over * 2, crownH).fill(ink.face);
  g.rect(sx - over, shaftTop - crownH * 0.35, wpx + over * 2, Math.max(1.5, crownH * 0.42)).fill(ink.coping);

  // Merlons round the parapet.
  if (z >= 13) {
    const count = Math.max(3, Math.round((wpx + over * 2) / Math.max(z * 0.34, 5)));
    const span = (wpx + over * 2) / count;
    const mh = Math.max(2, z * 0.2);
    for (let i = 0; i < count; i++) {
      const mx = sx - over + (i + 0.18) * span;
      g.rect(mx, shaftTop - crownH * 0.35 - mh, span * 0.64, mh).fill(ink.coping);
      g.rect(mx, shaftTop - crownH * 0.35 - mh, span * 0.64, Math.max(1, mh * 0.28)).fill(ink.joint);
    }
  }

  g.rect(sx - over, shaftTop - crownH * 0.35, wpx + over * 2, hpx + rise + crownH * 0.35).stroke({
    width: Math.max(1, z * 0.03),
    color: 0x141210,
    alpha: 0.85,
  });
}

/**
 * A gatehouse: two piers flanking the leaves, built like small towers.
 *
 * The baked gate texture is a good pair of timber leaves and, sitting flush in a
 * run of stone, it is indistinguishable from a wall tile at anything past
 * tactical zoom. Where the gates are is the first question anybody asks of a
 * circuit and whether the horde has found them is the second, so the piers exist
 * to give it a silhouette. The leaves themselves are left alone underneath;
 * this only frames them.
 */
export function drawGatehouse(
  g: Graphics,
  sx: number,
  sy: number,
  z: number,
  vertical: boolean,
  frac: number,
  vern: Vernacular,
): void {
  const ink = inkFor(3, frac, vern);
  const rise = z * 0.62;
  const pier = z * 0.36;
  const crownH = Math.max(2, z * 0.18);
  const over = z * 0.07;

  const draw = (px: number, py: number) => {
    g.rect(px + z * 0.09, py + z * 0.12, pier, pier).fill({ color: 0x000000, alpha: 0.36 });
    g.rect(px, py - rise, pier, pier + rise).fill(ink.face);
    g.rect(px, py - rise, pier * 0.4, pier + rise).fill(ink.top);
    g.rect(px - over, py - rise - crownH * 0.4, pier + over * 2, crownH).fill(ink.coping);
    if (z >= 15) {
      const mh = Math.max(1.5, z * 0.13);
      for (let i = 0; i < 2; i++) {
        g.rect(px - over + (i + 0.15) * ((pier + over * 2) / 2), py - rise - crownH * 0.4 - mh,
          (pier + over * 2) / 2 * 0.62, mh).fill(ink.coping);
      }
    }
    g.rect(px, py - rise, pier, pier + rise).stroke({
      width: Math.max(1, z * 0.03),
      color: 0x141210,
      alpha: 0.75,
    });
  };

  // Piers stand across the run: a gate in a north–south wall gets them east and
  // west of it, one in an east–west wall north and south.
  if (vertical) {
    draw(sx - pier * 0.55, sy + (z - pier) / 2);
    draw(sx + z - pier * 0.45, sy + (z - pier) / 2);
  } else {
    draw(sx + (z - pier) / 2, sy - pier * 0.4);
    draw(sx + (z - pier) / 2, sy + z - pier * 0.6);
  }
}
